import { render, h, Fragment } from "preact";
import { useState, useRef } from "preact/hooks";
import FormStepLayout from "../../templates/FormStepLayout/FormStepLayout";
import RightSidebar from "../../components/sidebars/RightSidebar";
import useFormStore from "../../store/useFormStore";
import ConfigureServicesDetails from "./ConfigureServicesComponents/ConfigureServicesDetails";
import ConfigurationStatusMessage from "./ConfigureServicesComponents/ConfigurationStatusMessage";

export default function ConfigureServices({ onBack, onNext }) {
  // Get data from store
  const currentFranchise = useFormStore((state) => state.currentFranchise);
  const selectedServices =
    useFormStore((state) => state.selectedServices) || [];
  const selectedServicesConfiguration = useFormStore(
    (state) => state.selectedServicesConfiguration,
  );
  const getConfiguredLineItems = useFormStore(
    (state) => state.getConfiguredLineItems,
  );
  const getServiceById = useFormStore((state) => state.getServiceById);

  // Check if franchise uses pricing
  const usesPricing = currentFranchise?.uses_pricing !== false;

  // Helper to check if a specific service is configured
  const getServiceStatus = (serviceId) => {
    const service = getServiceById(serviceId);
    const serviceConfig = service?.form;

    // If service type is "message", skip validation (consider it valid)
    if (serviceConfig?.type === "message") {
      return { isConfigured: true, isRequired: true };
    }

    // For "table" type services, check if at least one option is configured
    const serviceData = selectedServicesConfiguration[serviceId];
    if (!serviceData) {
      return { isConfigured: false, isRequired: true };
    }

    const hasConfiguration = Object.values(serviceData).some((row) =>
      Object.values(row).some((value) => value > 0),
    );

    return { isConfigured: hasConfiguration, isRequired: true };
  };

  // Check if ALL selected services are message-type (no configurable services)
  const allServicesAreMessageType = selectedServices.every((serviceId) => {
    const service = getServiceById(serviceId);
    return service?.form?.type === "message";
  });

  // Check if all selected services have at least one option configured.
  // Used to enable/disable Next button.
  // Only validate services with type "table", skip "message" type services
  const serviceIsSelected = selectedServices.every((serviceId) => {
    const service = getServiceById(serviceId);
    const serviceConfig = service?.form;

    // If service type is "message", skip validation (consider it valid)
    if (serviceConfig?.type === "message") {
      return true;
    }

    // For "table" type services, check if at least one option is configured
    const serviceData = selectedServicesConfiguration[serviceId];
    if (!serviceData) return false;

    return Object.values(serviceData).some((row) =>
      Object.values(row).some((value) => value > 0),
    );
  });

  // Disable Next button if all services are message-type (require at least one configurable service)
  const canProceed = serviceIsSelected && !allServicesAreMessageType;

  // Get all services with their completion status for the status message
  // Filter to selected services and maintain franchise array order
  const servicesWithStatus = (currentFranchise?.services || [])
    .filter((service) => {
      // Only include selected services that are configurable (not message type)
      return (
        selectedServices.includes(service.id) &&
        service?.form?.type !== "message"
      );
    })
    .map((service) => ({
      name: service.name || service.id,
      isComplete: getServiceStatus(service.id).isConfigured,
    }));

  // Filter franchise services based on selected service IDs, maintaining franchise array order
  const getServiceConfigs = () => {
    if (!currentFranchise?.services) return [];

    return currentFranchise.services
      .filter(
        (service) => selectedServices.includes(service.id) && service.form,
      )
      .map((service) => ({
        id: service.id,
        ...service,
      }));
  };

  // Get service configurations for selected services
  const serviceConfigs = getServiceConfigs();

  // Only show the warning if user tries to
  // advance without filling everything in.
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);
  const warningRef = useRef(null);

  const scrollToWarning = () => {
    if (!warningRef.current) return;
    const stickyHeader = document.querySelector(".sticky-header");
    const offset = (stickyHeader?.offsetHeight ?? 0) + 16;
    const targetY =
      warningRef.current.getBoundingClientRect().top +
      window.scrollY -
      offset;
    window.scrollTo({ top: targetY, behavior: "smooth" });
  };

  const handleNext = () => {
    if (canProceed) {
      setShowIncompleteWarning(false);
      onNext();
    } else {
      setShowIncompleteWarning(true);
      requestAnimationFrame(scrollToWarning);
    }
  };

  return (
    <FormStepLayout
      onNext={handleNext}
      onBack={onBack}
      nextText="Next: Schedule Service"
      leftSidebar={true}
      rightSidebar={<RightSidebar showCartRequirements={true} />}
      title="What Can We Clean For You?"
    >
      <div className="space-y-8">
        {showIncompleteWarning && !canProceed && (
          <div ref={warningRef}>
            <ConfigurationStatusMessage
              services={servicesWithStatus}
              minimumNotMet={false}
              usesPricing={usesPricing}
            />
          </div>
        )}

        {serviceConfigs.length > 0 ? (
          serviceConfigs.map((service) => {
            const status = getServiceStatus(service.id);
            return (
              <div key={service.id}>
                <ConfigureServicesDetails
                  serviceId={service.id}
                  serviceConfig={service.form}
                  isConfigured={status.isConfigured}
                  isRequired={status.isRequired}
                />
              </div>
            );
          })
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">
              No services selected. Please go back and select services.
            </p>
          </div>
        )}
      </div>
    </FormStepLayout>
  );
}
